import React, {useEffect, useRef, useState} from 'react';
import {SignatureDateInputElementClass} from 'types/documentTemplateElement';
import {ElementProps} from 'types/documentTemplateElement';
import {buildLabel, checkError} from 'utils/elementUtils';
import {useIsDocumentElementDisabled} from 'hooks/useIsDocumentElementDisabled';
import SignatureInput from '../../inputs/SignatureInput';
import DateInput from '../../inputs/DateInput';
import SignatureCanvas from 'react-signature-canvas';
import {useSelector} from 'react-redux';
import {RootState} from '../../../store/store';

const SignatureDateInputElementComponent: React.FC<
  ElementProps<SignatureDateInputElementClass>
> = ({element, updateElement, mode}) => {
  const SignatureCanvasRef = useRef<SignatureCanvas>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const [SignatureWidth, setSignatureWidth] = useState<number>(0);
  const userAuth = useSelector((state: RootState) => state.auth.user);
  const signatureBase64 = useSelector(
    (state: RootState) => state.auth.base64Signature,
  );
  const initialValueRef = useRef(element.properties.value || '');
  const hasSavedSignature =
    element.properties.value && element.properties.value !== '';
  const isCurrentAdmin =
    userAuth && element.properties.supervisorIds?.includes(userAuth.id);
  const shouldShowSignature = hasSavedSignature || isCurrentAdmin;

  const finalValue = shouldShowSignature
    ? hasSavedSignature
      ? element.properties.value
      : isCurrentAdmin
        ? signatureBase64 || ''
        : ''
    : '';

  useEffect(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const width80 = Math.floor(containerWidth * 0.95);
      setSignatureWidth(width80);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (mode === 'view' || mode === 'admin') {
      const newUpdatedElement = new SignatureDateInputElementClass({
        ...element,
        properties: {
          ...element.properties,
          dateValue: e.target.value,
        },
      });
      updateElement(newUpdatedElement);
    }
    return;
  };

  const handleEnd = () => {
    if (mode === 'view' || mode === 'admin') {
      const dataURL = SignatureCanvasRef.current?.toDataURL('image/png') || '';
      const newElement = new SignatureDateInputElementClass({
        ...element,
        properties: {
          ...element.properties,
          value: dataURL,
        },
      });
      updateElement(newElement);
    }
  };

  const clearSignatureDate = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    SignatureCanvasRef.current?.clear();

    const newElement = new SignatureDateInputElementClass({
      ...element,
      properties: {
        ...element.properties,
        value: undefined,
      },
    });
    updateElement(newElement);
  };

  const disabled = useIsDocumentElementDisabled(element, mode);

  const renderDateInput = () => {
    return (
      <DateInput
        id={element.id}
        isClickDisabled={mode === 'preview'}
        disabled={mode === 'edit' || disabled}
        label={element.properties?.dateLabel || ''}
        type="date"
        value={element.properties?.dateValue || ''}
        onChange={handleChange}
        required={element.properties.required}
        placeholder="MM/DD/YYYY"
        allowPastDates={element.properties.allowPastDates}
      />
    );
  };

  let containerClass = ' w-full m-4 pt-4';
  if (mode === 'view' || mode === 'preview' || mode === 'admin') {
    containerClass = element.properties.showLabel
      ? 'w-full mb-2'
      : 'w-full mt-5 mb-2';
  }

  if (mode === 'admin') {
    if (isCurrentAdmin || finalValue) {
      return (
        <div className="prose max-w-none w-full px-3 pb-2">
          {element.properties.showLabel && element.properties.label && (
            <label className="font-semibold">{element.properties.label}</label>
          )}
          <div className="px-3 pb-2 border-b border-slate-200">
            {finalValue && <img src={finalValue} alt="Signature" />}
            {!finalValue && isCurrentAdmin && (
              <span>No signature available</span>
            )}
          </div>
          {element.properties.subText && (
            <div className="flex justify-between items-center text-sm text-slate-800 mt-1 mb-2">
              <div>{element.properties.subText}</div>
            </div>
          )}
          {renderDateInput()}
        </div>
      );
    }
  }
  if (mode === 'view') {
    if (!disabled) {
      return (
        <div className="prose max-w-none w-full px-3 pb-2">
          {element.properties.showLabel && element.properties.label && (
            <label className="font-semibold">{element.properties.label}</label>
          )}
          <div className="px-3 pb-2 border-b border-slate-200">
            {element.properties.value || signatureBase64 ? (
              <img
                src={element.properties.value || signatureBase64}
                alt="Signature"
              />
            ) : (
              <span>No signature available</span>
            )}
          </div>
          {element.properties.subText && (
            <div className="flex justify-between items-center text-sm text-slate-800 mt-1 mb-2">
              <div>{element.properties.subText}</div>
            </div>
          )}
          {renderDateInput()}
        </div>
      );
    }
  }

  return (
    <div ref={containerRef} className={containerClass}>
      {!initialValueRef.current && (
        <>
          <SignatureInput
            width={SignatureWidth}
            error={checkError(element, mode)}
            label={buildLabel(element)}
            signatureCanvasRef={SignatureCanvasRef}
            disabled={disabled}
            onEnd={handleEnd}
            isClickDisabled={mode === 'preview'}
          />
          {element.properties.subText && (
            <div className="flex justify-between items-center text-sm text-slate-800 mt-1 mb-2 ">
              <div>{element.properties.subText}</div>

              {(mode === 'view' || mode === 'admin') && (
                <button
                  type="submit"
                  onClick={clearSignatureDate}
                  className="inline-flex items-center text-sm font-light text-slate-400
                       font-['Inter'] bg-white disabled:pointer-events-none disabled:bg-zinc-400
                       dark:focus:outline-none dark:focus:ring-1 dark:focus:ring-neutral-600">
                  Clear
                </button>
              )}
            </div>
          )}
          {renderDateInput()}
        </>
      )}
      {initialValueRef.current && (
        <div className="prose max-w-none w-full px-3 pb-2">
          <label className="block grow shrink basis-0  text-slate-800 text-sm font-semibold font-['Inter'] dark:text-white">
            {buildLabel(element)}
          </label>
          <div className="px-3 pb-2 border-b border-slate-200">
            <img src={finalValue} alt="SignatureDate" />
          </div>
          {element.properties.subText && (
            <div className="flex justify-between items-center text-sm text-slate-800 mt-1 mb-2">
              <div>{element.properties.subText}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SignatureDateInputElementComponent;
